import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Grid,
  LinearProgress,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import CookieOutlinedIcon from "@mui/icons-material/CookieOutlined";
import { api } from "../api/axios";
import { PageHeader, Pill, SectionCard, StatTile } from "../components/ui";

const noColon = (x) => String(x || "").replace(/[:：]\s*$/, "");

function fmt(n) {
  return Number(n || 0).toLocaleString();
}

export default function UsageDashboard() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/api/usage/overview")
      .then(({ data: d }) => setData(d))
      .catch((err) => setError(err?.response?.data?.message || "שגיאה"));
  }, []);

  if (error) {
    return (
      <Alert severity="error">
        {error}
      </Alert>
    );
  }

  if (!data)
    return (
      <Stack spacing={2}>
        <Skeleton variant="rounded" height={60} width="40%" />
        <Skeleton variant="rounded" height={120} />
        <Skeleton variant="rounded" height={300} />
      </Stack>
    );

  const plan = data.plan;
  const perSiteIncluded = Number(plan?.pageviews_included_monthly || 0);
  const ent = data.entitlements || {};
  const effectivePerSiteIncluded = perSiteIncluded + Number(ent.extra_pageviews_monthly || 0);
  const privacyStats = data.privacy?.by_site || [];
  const privacyMap = new Map(privacyStats.map((p) => [Number(p.site_id), p]));

  const privacyTotals = privacyStats.reduce(
    (acc, p) => {
      acc.views += Number(p.views || 0);
      acc.accept_all += Number(p.accept_all || 0);
      acc.reject_all += Number(p.reject_all || 0);
      acc.custom_save += Number(p.custom_save || 0);
      return acc;
    },
    { views: 0, accept_all: 0, reject_all: 0, custom_save: 0 }
  );
  const totalConsents =
    privacyTotals.accept_all + privacyTotals.reject_all + privacyTotals.custom_save;
  const totalAcceptRate = totalConsents
    ? Math.round((privacyTotals.accept_all / totalConsents) * 100)
    : 0;

  return (
    <Box>
      <PageHeader
        title={t("usage_title")}
        subtitle={
          <>
            {t("usage_month_label")} <b>{data.ym}</b> · {t("usage_metric_desc")}
          </>
        }
      />

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatTile icon={<LanguageOutlinedIcon />} label={noColon(t("usage_sites_count"))} value={fmt(data.totals.sites_count)} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatTile icon={<BarChartRoundedIcon />} tone="violet" label={noColon(t("usage_total_hits"))} value={fmt(data.totals.total_widget_config_hits)} hint={data.ym} />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatTile
            icon={<SpeedRoundedIcon />}
            tone="teal"
            label={noColon(t("usage_quota_per_site"))}
            value={plan ? (effectivePerSiteIncluded ? fmt(effectivePerSiteIncluded) : t("usage_no_limit")) : "—"}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatTile
            icon={<CookieOutlinedIcon />}
            tone="warning"
            label="אחוז אישור עוגיות"
            value={totalConsents ? `${totalAcceptRate}%` : "—"}
            hint="30 יום אחרונים"
          />
        </Grid>
      </Grid>

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 3 }}>
        {plan ? (
          <>
            <Pill tone="primary">
              {t("usage_plan_label")} {plan.name} ({plan.interval})
            </Pill>
            {Number(plan.ai_credits_monthly || 0) > 0 ? (
              <Pill tone="neutral">
                {t("usage_ai_credits_month")} {fmt(plan.ai_credits_monthly)}
              </Pill>
            ) : null}
            {Number(ent.extra_sites || 0) > 0 ? (
              <Pill tone="violet">
                {t("usage_extra_sites")} +{fmt(ent.extra_sites)}
              </Pill>
            ) : null}
            {Number(ent.extra_pageviews_monthly || 0) > 0 ? (
              <Pill tone="violet">
                {t("usage_extra_traffic")} +{fmt(ent.extra_pageviews_monthly)}
              </Pill>
            ) : null}
            {Number(ent.extra_ai_credits_monthly || 0) > 0 ? (
              <Pill tone="violet">
                {t("usage_extra_ai")} +{fmt(ent.extra_ai_credits_monthly)}
              </Pill>
            ) : null}
          </>
        ) : (
          <Pill tone="warning">{t("usage_no_subscription")}</Pill>
        )}
      </Stack>

      <SectionCard title={t("usage_title")} description={t("usage_metric_desc")} icon={<BarChartRoundedIcon />} contentSx={{ p: 0 }} sx={{ mb: 3 }}>
        <TableContainer>
          <Table sx={{ "& td, & th": { px: 3 } }}>
            <TableHead>
              <TableRow>
                <TableCell>{t("usage_table_site")}</TableCell>
                <TableCell align="right">{t("usage_table_hits_month")}</TableCell>
                <TableCell>{t("usage_table_utilization")}</TableCell>
                <TableCell align="right">{t("usage_table_remaining")}</TableCell>
                <TableCell>{t("usage_table_status")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.sites.map((s) => {
                const hits = Number(s.widget_config_hits || 0);
                const pct = effectivePerSiteIncluded > 0 ? Math.min(100, Math.round((hits / effectivePerSiteIncluded) * 100)) : 0;
                return (
                  <TableRow key={s.site_id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>{s.name}</Typography>
                      <Typography variant="caption" component="div">
                        {s.domain}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {fmt(hits)}
                    </TableCell>
                    <TableCell sx={{ minWidth: 200 }}>
                      {perSiteIncluded > 0 ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                          <Box sx={{ flexGrow: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={pct}
                              color={pct >= 90 ? "error" : pct >= 70 ? "warning" : "primary"}
                              aria-label={`${t("usage_table_utilization")} ${pct}%`}
                            />
                          </Box>
                          <Typography variant="caption" sx={{ minWidth: 34, fontWeight: 600 }}>
                            {pct}%
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{effectivePerSiteIncluded > 0 ? fmt(s.remaining_per_site) : "—"}</TableCell>
                    <TableCell>
                      <Pill tone={s.limit_exceeded ? "error" : s.status === "active" ? "success" : "neutral"}>
                        {s.limit_exceeded ? t("usage_status_exceeded") : s.status}
                      </Pill>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>

      <SectionCard
        title="דוחות שימוש בבלון הפרטיות (30 יום אחרונים)"
        description="סיכום צפיות והסכמות לפי אתר (Accept / Reject / Preferences)"
        icon={<CookieOutlinedIcon />}
        iconTone="teal"
        contentSx={{ p: 0 }}
      >
        <TableContainer>
          <Table sx={{ "& td, & th": { px: 3 } }}>
            <TableHead>
              <TableRow>
                <TableCell>אתר</TableCell>
                <TableCell align="right">צפיות</TableCell>
                <TableCell align="right">אישור הכל</TableCell>
                <TableCell align="right">דחייה</TableCell>
                <TableCell align="right">העדפות</TableCell>
                <TableCell align="right">אחוז אישור</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.sites.map((s) => {
                const p = privacyMap.get(Number(s.site_id)) || {};
                const views = Number(p.views || 0);
                const acceptAll = Number(p.accept_all || 0);
                const rejectAll = Number(p.reject_all || 0);
                const custom = Number(p.custom_save || 0);
                const consents = acceptAll + rejectAll + custom;
                const acceptRate = consents ? Math.round((acceptAll / consents) * 100) : 0;
                return (
                  <TableRow key={`privacy-${s.site_id}`} hover>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>{s.name}</Typography>
                      <Typography variant="caption" component="div">
                        {s.domain}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{fmt(views)}</TableCell>
                    <TableCell align="right">{fmt(acceptAll)}</TableCell>
                    <TableCell align="right">{fmt(rejectAll)}</TableCell>
                    <TableCell align="right">{fmt(custom)}</TableCell>
                    <TableCell align="right">{consents ? `${acceptRate}%` : "—"}</TableCell>
                  </TableRow>
                );
              })}
              <TableRow sx={{ bgcolor: (th) => th.aw.softer, "& td": { fontWeight: 700, borderBottom: 0 } }}>
                <TableCell>סה״כ</TableCell>
                <TableCell align="right">{fmt(privacyTotals.views)}</TableCell>
                <TableCell align="right">{fmt(privacyTotals.accept_all)}</TableCell>
                <TableCell align="right">{fmt(privacyTotals.reject_all)}</TableCell>
                <TableCell align="right">{fmt(privacyTotals.custom_save)}</TableCell>
                <TableCell align="right">{totalConsents ? `${totalAcceptRate}%` : "—"}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>
    </Box>
  );
}
